import type { AnswerMap, ScoredResult } from './types';

interface DraftBody {
  uuid: string;
  schemaId: string;
  schemaVersion: string;
  startedAt: string;
  answers: AnswerMap;
  turnstileToken?: string;
}

interface SubmitBody extends DraftBody {
  intake: { email: string; name?: string; company?: string; role?: string; phone?: string };
  turnstileToken: string;
  honeypot?: string;
}

interface SubmitResponse {
  success: boolean;
  uuid: string;
  resultUrl: string;
}

export async function saveDraft(body: DraftBody): Promise<{ success: boolean }> {
  const res = await fetch('/api/assessments/draft', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok && res.status !== 429) throw new Error(`draft save failed: ${res.status}`);
  return res.json();
}

export async function submitFinal(body: SubmitBody): Promise<SubmitResponse> {
  const res = await fetch('/api/assessments/submit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'unknown' }));
    throw new Error(`submit failed: ${(err as { error?: string }).error ?? res.status}`);
  }
  return res.json();
}

export async function fetchResult(schemaId: string, uuid: string): Promise<ScoredResult> {
  const res = await fetch(
    `/api/assessments/results/${uuid}?schemaId=${encodeURIComponent(schemaId)}`,
  );
  if (!res.ok) throw new Error(`fetch result failed: ${res.status}`);
  const data = (await res.json()) as { result: ScoredResult };
  return data.result;
}

export async function fetchDraft(schemaId: string, uuid: string) {
  const res = await fetch(
    `/api/assessments/draft/${uuid}?schemaId=${encodeURIComponent(schemaId)}`,
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`fetch draft failed: ${res.status}`);
  const data = await res.json();
  return (data as { draft: { startedAt: string; answers: AnswerMap } }).draft;
}
